/**
 * Cuentas publicitarias (E2): lectura de `GET /getmetaadaccounts` /
 * `POST /updatemetaadaccounts` y reglas de selección. Funciones puras.
 * Ver docs/maps/meta-ads.md § Cuentas publicitarias.
 */
import { readMetaLinks } from './metaAdsMoney';
import { readMetaOpportunityScore } from './metaDelivery';

/** Máximo de cuentas seguidas (`MAX_TRACKED` de la API). */
export const META_MAX_TRACKED_ACCOUNTS = 20;

/** `account_status` de Meta ≠ 1 → texto corto (el resto: «Con problemas en Meta»). */
const ACCOUNT_STATUS_LABELS = {
  2: 'Deshabilitada en Meta',
  3: 'Pago pendiente en Meta',
  7: 'En revisión de Meta',
  8: 'Pago pendiente en Meta',
  9: 'Pago pendiente en Meta',
  100: 'Cerrándose en Meta',
  101: 'Cerrada en Meta',
};

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const count = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

/**
 * Una cuenta con defaults seguros.
 *
 * @param {object} raw Fila de `data`.
 * @returns {object|null} `null` si no trae id.
 */
export function normalizeMetaAdAccount(raw) {
  const { metaUrl, adAccountId } = readMetaLinks(raw);
  if (!adAccountId) return null;
  const status = Number(raw?.account_status);
  const isActive = typeof raw?.is_active === 'boolean' ? raw.is_active : status === 1;
  return {
    id: adAccountId,
    name: str(raw?.name) || `Cuenta ${adAccountId}`,
    currency: str(raw?.currency)?.toUpperCase() || null,
    accountStatus: Number.isFinite(status) ? status : null,
    isActive,
    statusLabel: isActive ? null : (ACCOUNT_STATUS_LABELS[status] || 'Con problemas en Meta'),
    isTracked: raw?.is_tracked === true,
    // Sin el campo, se asume disponible (la API siempre lo manda).
    isAvailable: raw?.is_available !== false,
    lastSyncedAt: str(raw?.last_synced_at),
    initialLoadDone: raw?.initial_load_done === true,
    activeCampaignsCount: count(raw?.active_campaigns_count),
    // Ajuste 6: puntaje de oportunidad de Meta (0–100; null → «—»). Solo lectura.
    opportunity: readMetaOpportunityScore(raw),
    recommendationsCount: count(raw?.recommendations_count),
    metaUrl,
  };
}

/**
 * Lista de la respuesta (`data` es el array). Seguidas primero, después por nombre
 * (mismo orden que la API).
 *
 * @param {object} res Envelope de getmetaadaccounts / updatemetaadaccounts.
 * @returns {object[]}
 */
export function readMetaAdAccounts(res) {
  const list = Array.isArray(res?.data) ? res.data : [];
  const seen = new Set();
  return list.map(normalizeMetaAdAccount).filter((acc) => {
    if (!acc || seen.has(acc.id)) return false;
    seen.add(acc.id);
    return true;
  });
}

/**
 * Ids seguidos hoy. Incluye las seguidas que ya no están disponibles: la API
 * (ajuste 2) las acepta y las mantiene seguidas con su historial.
 */
export function trackedAccountIds(accounts) {
  return (accounts || []).filter((a) => a.isTracked).map((a) => a.id);
}

/**
 * ¿Se puede marcar? Disponibles en Meta, o no disponibles que **ya estaban
 * seguidas** (se pueden destildar y volver a tildar antes de guardar). Una
 * no disponible nueva da 404 al guardar.
 */
export const isSelectableAccount = (acc) => Boolean(acc?.isAvailable || acc?.isTracked);

/** Seguida, disponible y sin la carga de 90 días (la próxima sync la trae). */
export const isInitialLoadPending = (acc) => Boolean(acc?.isTracked && acc?.isAvailable && !acc?.initialLoadDone);

/**
 * Cambia una cuenta en la selección respetando el máximo.
 *
 * @param {string[]} selected
 * @param {string} id
 * @returns {string[]} Misma referencia si no cambia (máximo alcanzado).
 */
export function toggleAccountSelection(selected, id, max = META_MAX_TRACKED_ACCOUNTS) {
  if (selected.includes(id)) return selected.filter((x) => x !== id);
  if (selected.length >= max) return selected;
  return [...selected, id];
}

/** ¿La selección difiere de lo seguido hoy? (orden no importa) */
export function isSelectionDirty(selected, accounts) {
  const current = new Set(trackedAccountIds(accounts));
  return selected.length !== current.size || selected.some((id) => !current.has(id));
}

/** ¿La cuenta ya trajo datos? (primera carga hecha, última sync o campañas activas) */
export const accountHasData = (acc) => Boolean(acc?.initialLoadDone || acc?.lastSyncedAt || acc?.activeCampaignsCount > 0);

/**
 * Cuentas seguidas hoy que la selección deja de seguir. `unavailableTracked`:
 * las que ya no están disponibles en Meta (si se destildan, no se pueden
 * volver a sumar hasta que el dueño las comparta de nuevo).
 *
 * @returns {{ removed: object[], removedWithData: object[], unavailableTracked: object[] }}
 */
export function describeSelectionChange(selected, accounts) {
  const chosen = new Set(selected);
  const removed = (accounts || []).filter((a) => a.isTracked && !chosen.has(a.id));
  return {
    removed,
    removedWithData: removed.filter(accountHasData),
    unavailableTracked: removed.filter((a) => !a.isAvailable),
  };
}

/** Monedas distintas de la selección (para avisar que no se suman). */
export function selectedCurrencies(selected, accounts) {
  const chosen = new Set(selected);
  return [...new Set((accounts || []).filter((a) => chosen.has(a.id) && a.currency).map((a) => a.currency))].sort();
}
